/**
 * DuplicatesView — the admin-only "Duplikate" audit table (DMS Part 2, P2-12,
 * dms/spec-part2-stefan-delta.md §2.4). Rendered by `DocumentsScreen` when the
 * admin-gated "Duplikate" pill is active (`useEffectivePermissions().isWildcard`,
 * mirroring the server's `@Roles('admin')` gate on `GET /documents/duplicates`).
 *
 * Read-only — no selection, no batch bar, no upload: this is an audit trail
 * (the WHY a document was excluded from the productive stock), not a working
 * list. Every row: the duplicate doc (clickable, `/ablage/$id`, "Duplikat"
 * badge), Bezug, Quelle, Datum, "Duplikat von" → the ORIGINAL (clickable link,
 * `duplicate_of_document_id`/`matched_document_id`), Methode, Score, Erkannt am.
 *
 * [Source: apps/web/src/features/documents/DocumentsScreen.tsx] (columns/clickability precedent)
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §2.4, P2-12]
 */
import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { DataTable } from '../../../components/data/DataTable';
import { Badge } from '../../../components/ui/Badge';
import { EntityLink } from '../../../components/ui/EntityLink';
import { dateShortDe, dateTimeShortDe } from '../../../lib/format';
import { methodLabel, scoreDisplay } from '../api/documents-api';
import type { DuplicateRow } from '../hooks/useDuplicateRows';

export function DuplicatesView({
  rows,
  isLoading,
  isError,
}: {
  rows: DuplicateRow[];
  isLoading: boolean;
  isError: boolean;
}) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<DuplicateRow>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Dokument',
        // Clickability rule (dms/spec.md §4.6, carried into the Duplikate view):
        // ONLY the Dokument cell navigates.
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <EntityLink
              onActivate={() =>
                void navigate({ to: '/ablage/$id', params: { id: row.original.id } })
              }
              className="font-medium"
            >
              {row.original.title}
            </EntityLink>
            <Badge variant="warning">Duplikat</Badge>
          </div>
        ),
      },
      {
        accessorKey: 'bezug',
        header: 'Bezug',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.bezug ?? '-'}</span>
        ),
      },
      {
        accessorKey: 'quelle',
        header: 'Quelle',
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.quelle}</span>,
      },
      {
        accessorKey: 'doc_date',
        header: 'Datum',
        cell: ({ row }) => (
          <span className="mono-num text-xs">{dateShortDe(row.original.doc_date)}</span>
        ),
      },
      {
        id: 'original',
        header: 'Duplikat von',
        cell: ({ row }) => (
          <EntityLink
            onActivate={() =>
              void navigate({ to: '/ablage/$id', params: { id: row.original.original_id } })
            }
          >
            {row.original.original_title ?? 'Original ansehen'}
          </EntityLink>
        ),
      },
      {
        accessorKey: 'method',
        header: 'Methode',
        cell: ({ row }) => <Badge variant="neutral">{methodLabel(row.original.method)}</Badge>,
      },
      {
        accessorKey: 'score',
        header: 'Score',
        cell: ({ row }) => (
          <div className="text-right mono-num">{scoreDisplay(row.original.score)}</div>
        ),
      },
      {
        accessorKey: 'detected_at',
        header: 'Erkannt am',
        cell: ({ row }) => (
          <span className="mono-num text-xs text-muted-foreground">
            {dateTimeShortDe(row.original.detected_at)}
          </span>
        ),
      },
    ],
    [navigate],
  );

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
        Lade Duplikate …
      </div>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground"
      >
        Duplikate konnten nicht geladen werden.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
        Keine Duplikate erkannt.
      </div>
    );
  }

  return <DataTable columns={columns} data={rows} />;
}
